/**
 * Buffers incoming text chunks and releases characters in smooth
 * ticks (~60 fps) to eliminate visual choppiness.
 *
 * Algorithm:
 * 1. First chunk → buffer it, start a safety timeout.
 * 2. Second chunk → measure interval, begin releasing at a tick rate
 *    that drains the buffer before the next chunk is expected.
 * 3. Subsequent chunks → update interval estimate (EMA), dynamically
 *    recalculate how many characters to release per tick.
 *
 * The key insight: instead of one character per variable-delay timeout,
 * we use a fixed ~16 ms tick and vary how many characters are released
 * per tick. This naturally scales from slow streams (1 char/tick) to
 * fast streams (many chars/tick) without falling behind.
 */
export class StreamTextSmoother {
  private buffer: string = "";
  private displayedText: string = "";

  private lastChunkTime: number | null = null;
  private estimatedIntervalMs: number | null = null;
  private chunkCount: number = 0;

  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private isReleasing: boolean = false;

  /** ~60 fps tick rate for smooth visual updates */
  private static readonly TICK_INTERVAL_MS = 16;
  /** Aim to drain the buffer in 80% of the estimated chunk interval */
  private static readonly DRAIN_FACTOR = 0.8;
  /** If only one chunk arrives, wait this long before releasing with a fallback rate */
  private static readonly SAFETY_TIMEOUT_MS = 2000;
  /** Fallback: 1 char per tick when no timing estimate is available */
  private static readonly DEFAULT_CHARS_PER_TICK = 1;
  /** EMA smoothing factor for chunk interval estimation */
  private static readonly EMA_ALPHA = 0.3;

  constructor(private readonly onUpdate: (fullText: string) => void) {}

  /**
   * Accepts a new chunk from the streaming source.
   */
  public push(chunk: string): void {
    if (!chunk.length) {
      return;
    }

    const now = performance.now();
    this.buffer += chunk;
    this.chunkCount++;

    if (this.chunkCount === 1) {
      this.lastChunkTime = now;
      this.startSafetyTimeout();
      return;
    }

    // Second or subsequent chunk — update timing estimate
    const interval = now - this.lastChunkTime!;
    this.lastChunkTime = now;

    if (this.estimatedIntervalMs === null) {
      this.estimatedIntervalMs = interval;
    } else {
      this.estimatedIntervalMs =
        StreamTextSmoother.EMA_ALPHA * interval + (1 - StreamTextSmoother.EMA_ALPHA) * this.estimatedIntervalMs;
    }

    this.clearSafetyTimeout();

    if (!this.isReleasing) {
      this.startRelease();
    }
  }

  /**
   * Immediately release all remaining buffered text (used when streaming ends).
   */
  public flush(): void {
    this.clearSafetyTimeout();
    this.stopRelease();

    if (this.buffer.length > 0) {
      this.displayedText += this.buffer;
      this.buffer = "";
      this.onUpdate(this.displayedText);
    }
  }

  /**
   * Immediately clear all state (used on abort/error).
   */
  public reset(): void {
    this.clearSafetyTimeout();
    this.stopRelease();
    this.buffer = "";
    this.displayedText = "";
    this.lastChunkTime = null;
    this.estimatedIntervalMs = null;
    this.chunkCount = 0;
    this.isReleasing = false;
  }

  /**
   * Returns the full text that has been released so far (for testing/inspection).
   */
  public get currentText(): string {
    return this.displayedText;
  }

  /**
   * Returns true if there is buffered text not yet released.
   */
  public get hasPendingText(): boolean {
    return this.buffer.length > 0;
  }

  /**
   * Calculate how many characters to release in the current tick.
   * Scales dynamically: for slow streams → 1 char/tick, for fast → many chars/tick.
   */
  private calculateCharsPerTick(): number {
    if (this.estimatedIntervalMs === null || this.estimatedIntervalMs <= 0) {
      return StreamTextSmoother.DEFAULT_CHARS_PER_TICK;
    }

    // How many ms we have to drain the current buffer before the next chunk arrives
    const targetDrainMs = this.estimatedIntervalMs * StreamTextSmoother.DRAIN_FACTOR;
    // How many ticks fit in that window
    const ticksAvailable = targetDrainMs / StreamTextSmoother.TICK_INTERVAL_MS;

    if (ticksAvailable <= 1) {
      return this.buffer.length;
    }

    return Math.max(1, Math.ceil(this.buffer.length / ticksAvailable));
  }

  private startRelease(): void {
    this.isReleasing = true;
    this.releaseNext();
  }

  private releaseNext(): void {
    if (this.buffer.length === 0) {
      this.isReleasing = false;
      return;
    }

    const count = Math.min(this.calculateCharsPerTick(), this.buffer.length);
    this.displayedText += this.buffer.substring(0, count);
    this.buffer = this.buffer.substring(count);
    this.onUpdate(this.displayedText);

    if (this.buffer.length > 0) {
      this.releaseTimer = setTimeout(() => this.releaseNext(), StreamTextSmoother.TICK_INTERVAL_MS);
    } else {
      this.isReleasing = false;
    }
  }

  private stopRelease(): void {
    if (this.releaseTimer !== null) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    this.isReleasing = false;
  }

  private startSafetyTimeout(): void {
    this.safetyTimer = setTimeout(() => {
      if (this.chunkCount === 1 && this.buffer.length > 0 && !this.isReleasing) {
        this.startRelease();
      }
    }, StreamTextSmoother.SAFETY_TIMEOUT_MS);
  }

  private clearSafetyTimeout(): void {
    if (this.safetyTimer !== null) {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }
  }
}
